'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Truck,
  ClipboardList,
  CheckCircle2,
  AlertCircle,
  Copy,
} from 'lucide-react';
import { VENDORS } from '@/lib/mockData';
import { setCurrentVendor } from '@/lib/authStore';

type LoginFormValues = {
  email: string;
  password: string;
};

const DEMO_ACCOUNTS = VENDORS.map((v) => ({
  role: v.name,
  email: v.email,
  password: v.password,
  contactPerson: v.contactPerson,
}));

export default function VendorLoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({ mode: 'onBlur' });

  function autofill(email: string, password: string) {
    setValue('email', email, { shouldValidate: true });
    setValue('password', password, { shouldValidate: true });
    setLoginError('');
  }

  function copyToClipboard(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 1500);
    });
  }

  async function onSubmit(data: LoginFormValues) {
    setIsLoading(true);
    setLoginError('');

    // BACKEND INTEGRATION POINT: Replace with POST /api/vendor/auth
    await new Promise((resolve) => setTimeout(resolve, 900));

    const vendor = VENDORS.find(
      (v) =>
        v.email.toLowerCase() === data.email.toLowerCase() &&
        v.password === data.password
    );

    if (!vendor) {
      setLoginError(
        'Invalid credentials — use the demo accounts below to sign in.'
      );
      setIsLoading(false);
      return;
    }

    setCurrentVendor(vendor);
    router.push('/po-list');
  }

  return (
    <div className="flex w-full min-h-screen">
      {/* ── Left Brand Panel ────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 bg-primary flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 translate-x-1/3 -translate-y-1/3"
          style={{ background: 'radial-gradient(circle, white, transparent)' }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-5 -translate-x-1/3 translate-y-1/3"
          style={{ background: 'radial-gradient(circle, white, transparent)' }} />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <AppLogo size={40} />
          <span className="text-xl font-bold text-primary-foreground tracking-tight">
            VendorGRN
          </span>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary-foreground opacity-60 mb-3">
            Medical Supply Chain
          </p>
          <h1 className="text-3xl xl:text-4xl font-bold text-primary-foreground leading-tight mb-4">
            Deliver with precision.
            <br />
            Track every batch.
          </h1>
          <p className="text-primary-foreground opacity-75 text-base leading-relaxed max-w-sm">
            Submit Goods Receipt Notes, track partial fulfillments per line item,
            and maintain complete batch traceability — all from one portal.
          </p>

          {/* Feature list */}
          <ul className="mt-8 space-y-3">
            {[
              { icon: <ClipboardList size={16} />, text: 'Real-time remaining quantity per PO line' },
              { icon: <Truck size={16} />, text: 'Partial delivery with batch & expiry tracking' },
              { icon: <ShieldCheck size={16} />, text: 'Instant GRN reference on submission' },
            ].map((item) => (
              <li
                key={`feature-${item.text.slice(0, 20)}`}
                className="flex items-start gap-3 text-primary-foreground"
              >
                <span className="mt-0.5 opacity-80">{item.icon}</span>
                <span className="text-sm opacity-85">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer note */}
        <div className="relative z-10">
          <p className="text-primary-foreground opacity-40 text-xs">
            Prototype — in-memory data only. Production requires vendor auth + database.
          </p>
        </div>
      </div>

      {/* ── Right Form Panel ─────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="text-lg font-bold text-foreground tracking-tight">
              VendorGRN
            </span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">Vendor Sign In</h2>
            <p className="text-muted-foreground text-sm mt-1.5">
              Access your Purchase Orders and submit GRNs
            </p>
          </div>

          {/* Login error */}
          {loginError && (
            <div className="mb-5 flex items-start gap-2.5 p-3.5 rounded-lg bg-[var(--urgency-red-bg)] border border-[var(--urgency-red)] border-opacity-30 text-[var(--urgency-red)] text-sm fade-in">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{loginError}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="label-base">
                Vendor Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="warehouse@yourcompany.com"
                className={`input-base ${errors.email ? 'input-error' : ''}`}
                {...register('email', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-[var(--urgency-red)] flex items-center gap-1">
                  <AlertCircle size={11} />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="label-base">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`input-base pr-10 ${errors.password ? 'input-error' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-[var(--urgency-red)] flex items-center gap-1">
                  <AlertCircle size={11} />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full mt-2 py-3 text-sm"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                'Sign In to Portal'
              )}
            </button>
          </form>

          {/* Demo credentials box */}
          <div className="mt-8 p-4 rounded-xl bg-card border border-border">
            <p className="section-header mb-3">Demo Accounts — Click to Autofill</p>
            <div className="space-y-2">
              {DEMO_ACCOUNTS.map((acc) => (
                <div
                  key={`cred-${acc.email}`}
                  className="rounded-lg border border-border bg-background overflow-hidden"
                >
                  {/* Header row — clickable to autofill */}
                  <button
                    type="button"
                    onClick={() => autofill(acc.email, acc.password)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted transition-colors group text-left"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0">
                        {acc.contactPerson.charAt(0)}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">
                          {acc.role}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {acc.contactPerson}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Autofill →
                    </span>
                  </button>

                  {/* Credential rows */}
                  <div className="border-t border-border divide-y divide-border">
                    {[
                      { label: 'Email', value: acc.email, key: `email-${acc.email}` },
                      { label: 'Password', value: acc.password, key: `pwd-${acc.email}` },
                    ].map((field) => (
                      <div
                        key={field.key}
                        className="flex items-center justify-between px-3 py-1.5"
                      >
                        <span className="text-xs text-muted-foreground w-16 shrink-0">
                          {field.label}
                        </span>
                        <span className="text-xs font-mono text-foreground flex-1 truncate mx-2">
                          {field.value}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(field.value, field.key)}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                          title={`Copy ${field.label}`}
                          aria-label={`Copy ${field.label} for ${acc.role}`}
                        >
                          {copiedField === field.key ? (
                            <CheckCircle2 size={12} className="text-[var(--urgency-green)]" />
                          ) : (
                            <Copy size={12} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}